import { z } from 'zod';
import { clientBundleSchema } from '../api/services/users/schema';
import { campaignProductSchema, placeSchema, placeServicesSchema } from './shared';

export const bookDateSchema = z.object({
  dynamicPriceListIdKey: z.string().optional(),
  employees: z.any().optional(), // <- fix this
  selected: z.number().or(z.string()).optional(),
  timestamp: z.number().optional(),
  selectedCapacity: z.nullable(z.number()).default(1),
  capacity: z.number().nullable().optional(),
});

export type BookDate = z.infer<typeof bookDateSchema>;

export const giftcardSchema = z.object({
  giftCardType: z
    .union([
      z.literal('UGC'),
      z.literal('EGC'),
      z.literal('IGC'),
      z.literal('VCIGC'),
      z.literal('VCEGC'),
      z.literal('UGCW'),
    ])
    .optional(),
  giftCardExpiryDate: z.string().optional(),
  amount: z.number().optional(),
  code: z.string().optional(),
  formId: z.number().optional(),
  ownerCompanyId: z.number().or(z.string()).optional().nullable(),
});

export type Giftcard = z.infer<typeof giftcardSchema>;

export const giftcardsSchema = z.record(giftcardSchema);

export type Giftcards = z.infer<typeof giftcardsSchema>;

export const bookUUIDSchema = z.nullable(
  z.object({
    giftcards: giftcardsSchema.optional().nullable(),
    hash: z.string().optional(),
    uuid: z.string(),
  }),
);

const bookEmployeeSchema = z.number();

const campaignServiceSchema = z.object({
  discountPercent: z.number().optional().nullable(),
  discountPrice: z.number().optional().nullable(),
  id: z.number().optional().nullable(),
});

export const bookCampaignsSchema = z.object({
  campaignId: z.string().optional().nullable(),
  createdAt: z.string().optional().nullable(),
  endDate: z.number().optional().nullable(),
  id: z.number().optional().nullable(),
  name: z.string().optional().nullable(),
  place: z.number().optional().nullable(),
  services: z.array(campaignServiceSchema).optional().nullable(),
  startDate: z.number().optional().nullable(),
  status: z.number().optional().nullable(),
  type: z.number().optional().nullable(),
  updatedAt: z.string().optional().nullable(),
  publicationType: z.number().nullable().optional(),
  products: z.array(campaignProductSchema).optional().nullable(),
});

export type BookCampaign = z.infer<typeof bookCampaignsSchema>;

export const EXTRA_TIME_REQUIREMENT = {
  NotAnsweredYet: 0,
  NoExtraTime: 1,
  NeedsExtraTime: 2,
} as const;

export type ExtraTimeRequirement = (typeof EXTRA_TIME_REQUIREMENT)[keyof typeof EXTRA_TIME_REQUIREMENT];

export const bookStateSchema = z.object({
  availability: z.any().optional(), // <- fix this
  campaigns: z.array(bookCampaignsSchema).optional().nullable(),
  employee: bookEmployeeSchema.optional().nullable(),
  place: placeSchema,
  services: placeServicesSchema,
  time: bookDateSchema,
  usageReqId: bookUUIDSchema.optional().nullable(),
  fromSsr: z.boolean().optional(),
  saved: z.boolean().optional(),
  saving: z.boolean().optional(),
  failure: z.string().optional(),
  errorGiftCard: z.string().optional(),
  successGiftCard: z.boolean().optional(),
  validateGiftCard: z.boolean().optional(),
  keepEmployee: z.boolean().optional(),
  priceId: z.string().optional(),
  appliedBundle: clientBundleSchema.optional(),
  /**
   * hasRemovedAppliedBundle is used for tracking purposes to know if the user has removed an applied bundle
   * during the booking process in checkout
   */
  hasRemovedAppliedBundle: z.boolean().optional(),
  changeTimeBookingId: z.number().optional(),
  extraTimeRequirement: z
    .union([z.literal(EXTRA_TIME_REQUIREMENT.NoExtraTime), z.literal(EXTRA_TIME_REQUIREMENT.NeedsExtraTime)])
    .nullable()
    .optional(),
});
export type BookState = z.infer<typeof bookStateSchema>;
